import React, { useEffect, useMemo, useRef } from 'react';
import usePlacesAutocomplete, { getGeocode, getLatLng } from 'use-places-autocomplete';
import { Form, ListGroup } from 'react-bootstrap';

const AddressAutocomplete = ({ value, onChange, onSelect, placeholder, countryCode, required = false, disabled = false }) => {
  const requestOptions = useMemo(() => {
    if (countryCode && typeof countryCode === 'string' && countryCode.trim().length === 2) {
      return { componentRestrictions: { country: countryCode.trim().toLowerCase() } };
    }
    return {};
  }, [countryCode]);

  const {
    ready,
    value: inputValue,
    suggestions: { status, data },
    setValue,
    clearSuggestions,
    init,
  } = usePlacesAutocomplete({
    requestOptions,
    debounce: 250,
    initOnMount: true,
  });

  const hasSelectedRef = useRef(false);

  // Si Google Maps termina de cargar después de montar el componente, inicializar usePlacesAutocomplete
  useEffect(() => {
    if (!ready && typeof window !== 'undefined' && window.google?.maps?.places) {
      init();
    }
  }, [ready, init]);

  // Sync external value with internal state
  useEffect(() => {
    if (value !== undefined && value !== inputValue) {
      setValue(value || "", false);
    }
  }, [value, setValue, inputValue]);

  const handleInput = (e) => {
    setValue(e.target.value);
    hasSelectedRef.current = false;
    onChange(e.target.value);
  };

  const handleSelect = async ({ description, place_id }) => {
    setValue(description, false);
    clearSuggestions();
    hasSelectedRef.current = true;
    onChange(description); // Actualizar texto en padre

    if (onSelect) {
      try {
        const results = place_id
          ? await getGeocode({ placeId: place_id })
          : await getGeocode({ address: description });

        if (results && results.length > 0) {
          const { lat, lng } = await getLatLng(results[0]);
          onSelect({ address: description, lat, lng, place_id });
        } else {
          onSelect({ address: description, lat: null, lng: null });
        }
      } catch (error) {
        console.error("Error geocodificando selección por placeId, intentando por dirección: ", error);
        try {
          const fallbackResults = await getGeocode({ address: description });
          const { lat, lng } = await getLatLng(fallbackResults[0]);
          onSelect({ address: description, lat, lng, place_id });
        } catch (fallbackError) {
          console.error("Error final geocodificando selección: ", fallbackError);
          onSelect({ address: description, lat: null, lng: null });
        }
      }
    }
  };

  return (
    <div style={{ position: 'relative' }}>
      <Form.Control
        value={inputValue}
        onChange={handleInput}
        onBlur={() => {
          setTimeout(() => clearSuggestions(), 180);
        }}
        disabled={disabled}
        required={required}
        placeholder={placeholder || "Buscar dirección..."}
        autoComplete="off"
      />
      {status === "OK" && data && data.length > 0 && (
        <ListGroup
          style={{
            position: 'absolute',
            zIndex: 2050,
            width: '100%',
            maxHeight: '230px',
            overflowY: 'auto',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.16)',
            borderRadius: '0 0 8px 8px'
          }}
        >
          {data.map(({ place_id, description, structured_formatting }) => (
            <ListGroup.Item
              action
              key={place_id}
              onMouseDown={(e) => {
                e.preventDefault();
                handleSelect({ description, place_id });
              }}
              className="py-2 px-3 small d-flex align-items-start gap-2"
            >
              <span style={{ lineHeight: 1.3 }}>📍</span>
              <div style={{ lineHeight: 1.25 }}>
                {structured_formatting ? (
                  <>
                    <span className="fw-semibold text-dark">{structured_formatting.main_text}</span>
                    {structured_formatting.secondary_text && (
                      <span className="text-muted ms-1">{structured_formatting.secondary_text}</span>
                    )}
                  </>
                ) : (
                  <span>{description}</span>
                )}
              </div>
            </ListGroup.Item>
          ))}
        </ListGroup>
      )}
    </div>
  );
};

export default AddressAutocomplete;

